import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { P, roleLabels } from "@/lib/permissions";
import type { Paged, PlatformUser } from "@/lib/types";
import { Alert, Badge, Button, Card, Field, PageHeader, Pager, Skeleton, Table, cx } from "@/components/ui";
import { useAuth } from "@/state/auth";

const PAGE_SIZE = 25;

/**
 * The roles this admin may hand out, as the API computes them. Not derived here:
 * who may grant what is a seniority ladder that lives in Go, and a second copy
 * in TypeScript would drift the first time a role is added.
 */
interface AssignableRole {
  role: string;
  label: string;
  is_staff: boolean;
}

interface UsersPage extends Paged<PlatformUser> {
  assignable_roles?: AssignableRole[];
}

const EMPTY_NEW_USER = {
  email: "",
  first_name: "",
  last_name: "",
  phone: "",
  role: "",
  password: "",
  account_id: "",
};

export function PlatformUsers() {
  const { can, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [staffOnly, setStaffOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [target, setTarget] = useState<PlatformUser | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_NEW_USER });
  const [createError, setCreateError] = useState("");
  const [created, setCreated] = useState("");

  const query = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (search.trim()) query.set("q", search.trim());
  if (staffOnly) query.set("staff", "true");

  const { data, isLoading } = useQuery({
    queryKey: ["platform-users", search, staffOnly, offset],
    queryFn: () => api.get<UsersPage>(`/v1/admin/users?${query.toString()}`),
    placeholderData: keepPreviousData,
  });

  const assignableRoles = data?.assignable_roles ?? [];

  const createUser = useMutation({
    mutationFn: () =>
      api.post<PlatformUser>("/v1/admin/users", {
        ...draft,
        // Empty means the caller's own account, which is where WeShip staff
        // belong. Sent as omitted rather than as an empty string.
        account_id: draft.account_id.trim() || undefined,
      }),
    onSuccess: (user) => {
      setCreated(`${user.email} created as ${roleLabels[user.role] ?? user.role}.`);
      setDraft({ ...EMPTY_NEW_USER });
      setCreating(false);
      setCreateError("");
      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
    },
    onError: (err) =>
      setCreateError(err instanceof ApiError ? err.message : "Could not create the user."),
  });

  const deactivate = useMutation({
    mutationFn: (user: PlatformUser) =>
      api.patch<PlatformUser>(`/v1/admin/users/${user.id}`, { is_active: !user.is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["platform-users"] }),
  });

  const impersonate = useMutation({
    mutationFn: async () => {
      if (!target) return;
      await startImpersonation(target.id, reason);
    },
    onSuccess: () => {
      setTarget(null);
      setReason("");
      // Land on the customer's own overview: the point of impersonating is to
      // see what they see.
      navigate("/app");
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not start the session. Try again."),
  });

  const total = data?.meta.total ?? 0;

  return (
    <>
      <PageHeader title="Users" subtitle={total ? `${total} users across all accounts` : undefined} />

      {created && (
        <div className="mb-4">
          <Alert tone="good">{created}</Alert>
        </div>
      )}

      {can(P.platformUsers) && !creating && (
        <div className="mb-4">
          <Button
            onClick={() => {
              setCreating(true);
              setCreated("");
            }}
          >
            New user
          </Button>
        </div>
      )}

      {creating && (
        <Card className="mb-5">
          <h2 className="text-lg font-bold tracking-tight">New user</h2>
          <p className="mt-1 text-sm text-ink-500">
            Leave the account blank for WeShip staff, who belong to the platform account. Give an
            account id to add someone to a customer.
          </p>
          {createError && (
            <div className="mt-3">
              <Alert>{createError}</Alert>
            </div>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field
              label="Email"
              value={draft.email}
              onChange={(event) => setDraft({ ...draft, email: event.target.value })}
              placeholder="person@example.co.za"
            />
            <label className="block">
              <span className="ws-label">Role</span>
              <select
                className="ws-input mt-1"
                value={draft.role}
                onChange={(event) => setDraft({ ...draft, role: event.target.value })}
              >
                <option value="">Choose a role</option>
                {assignableRoles.map((role) => (
                  <option key={role.role} value={role.role}>
                    {role.label}
                    {role.is_staff ? " — WeShip staff" : ""}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label="First name"
              value={draft.first_name}
              onChange={(event) => setDraft({ ...draft, first_name: event.target.value })}
            />
            <Field
              label="Last name"
              value={draft.last_name}
              onChange={(event) => setDraft({ ...draft, last_name: event.target.value })}
            />
            <Field
              label="Phone"
              value={draft.phone}
              onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
              placeholder="0821234567"
            />
            <Field
              label="Account"
              value={draft.account_id}
              onChange={(event) => setDraft({ ...draft, account_id: event.target.value })}
              placeholder="Blank for WeShip staff"
              hint="An account id, from the Accounts screen."
            />
            <Field
              label="Password"
              type="password"
              value={draft.password}
              onChange={(event) => setDraft({ ...draft, password: event.target.value })}
              hint="At least 10 characters. Pass it on yourself and have them change it."
            />
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              loading={createUser.isPending}
              disabled={!draft.email.trim() || !draft.role || draft.password.length < 10}
              onClick={() => createUser.mutate()}
            >
              Create user
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setCreating(false);
                setCreateError("");
                setDraft({ ...EMPTY_NEW_USER });
              }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => {
            setStaffOnly(false);
            setOffset(0);
          }}
          className={cx(
            "rounded-lg px-3 py-1.5 text-sm font-medium",
            !staffOnly ? "bg-ink-900 text-white" : "bg-white text-ink-600 hover:bg-ink-100",
          )}
        >
          Everyone
        </button>
        <button
          onClick={() => {
            setStaffOnly(true);
            setOffset(0);
          }}
          className={cx(
            "rounded-lg px-3 py-1.5 text-sm font-medium",
            staffOnly ? "bg-ink-900 text-white" : "bg-white text-ink-600 hover:bg-ink-100",
          )}
        >
          WeShip staff
        </button>
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
          placeholder="Name or email"
          aria-label="Search users"
          className="ws-input ml-auto w-full sm:w-80"
        />
      </div>

      {target && (
        <Card className="mb-5 border-accent-strong/30 bg-accent-soft">
          <h2 className="text-lg font-bold tracking-tight">
            Act as {target.first_name} {target.last_name}
          </h2>
          <p className="mt-1 text-sm text-ink-700">
            You will see exactly what this user sees, with their permissions and nothing more. The
            session lasts 30 minutes, cannot be extended, and is recorded against your name.
          </p>
          {error && (
            <div className="mt-3">
              <Alert>{error}</Alert>
            </div>
          )}
          <Field
            label="Reason"
            className="mt-4 max-w-lg"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Ticket 4413, customer cannot see her waybill"
            hint="Recorded in the audit log. A ticket number is ideal."
          />
          <div className="mt-4 flex gap-2">
            <Button loading={impersonate.isPending} disabled={reason.trim().length < 8} onClick={() => impersonate.mutate()}>
              Start session
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setTarget(null);
                setError("");
              }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      ) : (
        <>
          <Table head={["User", "Account", "Role", "Last seen", ""]}>
            {(data?.data ?? []).map((user) => (
              <tr key={user.id} className="transition hover:bg-ink-50">
                <td className="ws-td">
                  <p className="font-semibold">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs text-ink-500">{user.email}</p>
                </td>
                <td className="ws-td text-ink-600">{user.account_name}</td>
                <td className="ws-td">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge tone={user.is_staff ? "attention" : "neutral"}>
                      {roleLabels[user.role] ?? user.role}
                    </Badge>
                    {!user.is_active && <Badge tone="bad">disabled</Badge>}
                  </div>
                </td>
                <td className="ws-td text-ink-500">
                  {user.last_login_at ? dateTime(user.last_login_at) : "Never"}
                </td>
                <td className="ws-td">
                  <div className="flex justify-end gap-2">
                    {/* Staff cannot be impersonated: borrowing another admin's
                        identity is how an audit trail gets laundered. */}
                    {can(P.impersonate) && !user.is_staff && user.is_active && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setTarget(user);
                          setError("");
                        }}
                      >
                        Act as
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant={user.is_active ? "danger" : "secondary"}
                      onClick={() => deactivate.mutate(user)}
                    >
                      {user.is_active ? "Disable" : "Enable"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
          <Pager total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
        </>
      )}
    </>
  );
}
